import type { Id } from "@convex/_generated/dataModel";
import type { AddressValue } from "@/components/inputs/address-input";
import type { FulfillmentType } from "@/components/quotes/listing-request/fulfillment-field";
import type { QuoteRequirementField } from "./listing-quote-requirements";

export interface GuestContactState {
  name: string;
  email: string;
  phone: string;
}

export type QuoteContactMode = "guest" | "account-profile" | "account";

export interface ListingRequestSummary {
  leadId: Id<"leads">;
  leadNumber: string;
  canCancel?: boolean;
}

export type QuoteTouchedFields = Partial<Record<QuoteRequirementField, true>>;

export interface QuoteFormState {
  contact: GuestContactState;
  quantity: number;
  quantityDisplay: string;
  fulfillmentType: FulfillmentType;
  preferredDate: string;
  datePickerOpen: boolean;
  notes: string;
  submittedLeadNumber: string | null;
  existingRequest: ListingRequestSummary | null;
  touched: QuoteTouchedFields;
  deliveryAddress: AddressValue;
}

export type UpdateQuoteFormState = (patch: Partial<QuoteFormState>) => void;

interface InitialQuoteFormValues {
  initialQuantity: number;
  initialLine1?: string;
  initialLine2?: string;
  initialCity?: string;
  initialState?: string;
  initialZip?: string;
  initialCountry?: string;
  initialDeliveryDate?: string;
  initialContact?: Partial<GuestContactState>;
}

export function buildInitialQuoteFormState(
  values: InitialQuoteFormValues,
): QuoteFormState {
  return {
    contact: {
      name: values.initialContact?.name ?? "",
      email: values.initialContact?.email ?? "",
      phone: values.initialContact?.phone ?? "",
    },
    quantity: values.initialQuantity,
    quantityDisplay: String(values.initialQuantity),
    fulfillmentType: "delivery",
    preferredDate: values.initialDeliveryDate ?? "",
    datePickerOpen: false,
    notes: "",
    submittedLeadNumber: null,
    existingRequest: null,
    touched: {},
    deliveryAddress: {
      line1: values.initialLine1 ?? "",
      line2: values.initialLine2 ?? "",
      city: values.initialCity ?? "",
      state: values.initialState ?? "",
      postalCode: values.initialZip ?? "",
      country: values.initialCountry ?? "US",
    },
  };
}

export function joinContactName(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
): string {
  return [firstName?.trim(), lastName?.trim()].filter(Boolean).join(" ");
}

export interface ContactNameProfile {
  firstName: string;
  lastName: string;
}

export function splitContactName(name: string): ContactNameProfile {
  const normalized = name.trim().replace(/\s+/g, " ");
  const lastSpace = normalized.lastIndexOf(" ");
  if (lastSpace === -1) return { firstName: normalized, lastName: "" };
  return {
    firstName: normalized.slice(0, lastSpace),
    lastName: normalized.slice(lastSpace + 1),
  };
}
